import { useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService } from '@services/authService';

const MICROFRONTEND_URL = (import.meta as any).env?.VITE_DB_MOD_MFE_URL || 'http://localhost:5175';
// postMessage targetOrigin must be scheme+host+port only (no path)
const MICROFRONTEND_ORIGIN = new URL(MICROFRONTEND_URL).origin;

/**
 * DBModernizationWrapper - Iframe wrapper for the DB Modernization microfrontend
 *
 * Handles:
 * - Embedding the microfrontend via iframe
 * - postMessage communication for authentication
 * - Navigation events from the microfrontend
 */
export function DBModernizationWrapper() {
  const navigate = useNavigate();
  const location = useLocation();
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const routePrefix = '/workspace/db-modernization';
  const pathSuffix = location.pathname.startsWith(routePrefix)
    ? location.pathname.substring(routePrefix.length)
    : '';
  const iframePath = pathSuffix || '/';
  const iframeSrc = `${MICROFRONTEND_URL.replace(/\/$/, '')}${iframePath}`;

  // Send auth token to iframe — always ensure freshness first
  const sendAuthToken = useCallback(async () => {
    try {
      await authService.getKeycloakInstance()?.updateToken(60);
    } catch {
      // Ignore
    }
    const token = authService.getToken();
    console.log('DBModernizationWrapper: Sending auth token to microfrontend:', token ? 'token present' : 'no token');

    if (!iframeRef.current?.contentWindow) {
      console.warn('DBModernizationWrapper: Iframe not ready yet');
      return;
    }

    try {
      iframeRef.current.contentWindow.postMessage(
        { type: 'AUTH_TOKEN', token: token || null },
        MICROFRONTEND_ORIGIN
      );
    } catch (error) {
      console.error('DBModernizationWrapper: Failed to send message to iframe:', error);
    }
  }, []);

  // Handle messages from iframe
  const handleMessage = useCallback(
    (event: MessageEvent) => {
      // Only accept messages from microfrontend origin
      if (event.origin !== MICROFRONTEND_ORIGIN) return;

      const { type, ...data } = event.data || {};

      switch (type) {
        case 'REQUEST_AUTH': {
          // Get auth token from Keycloak service and send it
          sendAuthToken();
          break;
        }

        case 'NAVIGATE': {
          // Navigate to a new path in the main app
          const path = data.path as string;
          if (path) {
            navigate(path);
          }
          break;
        }

        case 'CONVERSION_COMPLETED': {
          // Handle conversion completion notification
          const sessionId = data.sessionId as string;
          console.log('Conversion completed:', sessionId);
          break;
        }

        default:
          break;
      }
    },
    [navigate, sendAuthToken]
  );

  // Set up message listener
  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);

  // Push a fresh token every 4 minutes to keep long-running operations authenticated
  useEffect(() => {
    const interval = setInterval(() => {
      if (iframeRef.current) {
        sendAuthToken();
      }
    }, 4 * 60 * 1000);
    return () => clearInterval(interval);
  }, [sendAuthToken]);

  // Handle iframe load - proactively send auth token
  const handleIframeLoad = useCallback(() => {
    console.log('DBModernizationWrapper: Iframe loaded, sending auth token proactively');
    // Delay to ensure the iframe's content and message listener are fully set up
    setTimeout(() => {
      sendAuthToken();
    }, 500);
  }, [sendAuthToken]);

  return (
    <div className="h-[calc(100vh-64px)] w-full -m-8">
      <iframe
        ref={iframeRef}
        src={iframeSrc}
        title="DB Modernization Platform"
        className="w-full h-full border-0"
        allow="clipboard-write"
        onLoad={handleIframeLoad}
      />
    </div>
  );
}
